import { format } from "date-fns";
import { type EvaluationFullResponse } from "../api";
import { EvaluationCheckpoint } from "./evaluation_checkpoint";
import { Utility } from "./utility";

export type RecommendationBucket =
  "highly-recommend" | "recommend" | "not-recommend" | "error" | "unknown";

export type AvailabilityStripVariant =
  "positive" | "warning" | "followup" | "empty";

const NO_AVAILABILITY_CAPTURED_TEXT =
  "No availability was captured during the screening call.";

// Canonical display labels used wherever a bucket is shown to the user.
export const RECOMMENDATION_BUCKET_LABELS: Record<
  RecommendationBucket,
  string
> = {
  "highly-recommend": "Highly Recommend",
  recommend: "Recommend",
  "not-recommend": "Not Recommend",
  error: "Error",
  unknown: "Unavailable",
};

// Relative ordering used by the candidate-list sort comparator. Higher = better.
export const RECOMMENDATION_BUCKET_SCORES: Record<
  RecommendationBucket,
  number
> = {
  "highly-recommend": 5,
  recommend: 4,
  "not-recommend": 2,
  error: 0,
  unknown: -1,
};

/**
 * Map any backend `evaluation.recommendation` string — legacy or current,
 * conditional or not — to a canonical bucket. The backend has shipped multiple
 * naming variants over time:
 *   - Current `FinalRecommendation`: "Highly Recommend" / "Recommend" / "Not Recommend"
 *     (with optional " (Conditional)" suffix)
 *   - Legacy `EvaluationRecommendation`: "Highly Recommended" / "Recommended" /
 *     "Neutral" / "Not Recommended" / "Not Suitable"
 *
 * The current `FinalRecommendation` only emits three tiers, so legacy
 * "Neutral" is folded into "recommend" and legacy "Not Suitable" into
 * "not-recommend" so the UI shows a consistent three-bucket view.
 */
export const toRecommendationBucket = (
  recommendation: string | null | undefined,
): RecommendationBucket => {
  if (!recommendation) return "unknown";
  const normalized = recommendation
    .replace(/\s*\(Conditional\)\s*$/i, "")
    .trim()
    .toLowerCase();
  switch (normalized) {
    case "highly recommend":
    case "highly recommended":
      return "highly-recommend";
    case "recommend":
    case "recommended":
    case "neutral":
      return "recommend";
    case "not recommend":
    case "not recommended":
    case "not suitable":
      return "not-recommend";
    case "error":
      return "error";
    default:
      return "unknown";
  }
};

export interface Evaluation extends EvaluationFullResponse {}

export class Evaluation extends Utility<Evaluation> {
  constructor(init?: Partial<EvaluationFullResponse>) {
    super();
    Object.assign(this, init);
  }

  get recommendation_system(): string {
    return this.recommendation.toLowerCase().trim().replace(" ", "-");
  }

  /**
   * Returns the recommendation without the "(Conditional)" suffix, normalized to lowercase.
   */
  get normalized_recommendation(): string {
    return this.recommendation
      .replace(/\s*\(Conditional\)\s*$/i, "")
      .trim()
      .toLowerCase();
  }

  /** Canonical recommendation bucket, free of legacy/conditional/casing variation. */
  get recommendation_bucket(): RecommendationBucket {
    return toRecommendationBucket(this.recommendation);
  }

  get is_conditional_recommendation(): boolean {
    return /\(Conditional\)\s*$/i.test(this.recommendation ?? "");
  }

  /** Display text used by the candidate table's `aiAssessment` prop. */
  get recommendation_display_text(): string {
    switch (this.recommendation_bucket) {
      case "highly-recommend":
        return "Highly recommended";
      case "recommend":
        return "Recommended";
      case "not-recommend":
        return "Not recommended";
      default:
        return "Not recommended";
    }
  }

  get local_exported_at(): Date | null {
    if (!this.exported_at) return null;
    return this.get_local_time(this.exported_at);
  }

  get local_exported_at_string(): string | null {
    if (this.local_exported_at === null) return null;
    return format(this.local_exported_at, "MMM dd, yyyy hh:mm aa");
  }

  // Not Assessed, an unrecognized status, a missing row, or a blank answer all resolve
  // to the same empty-state copy across availability_caption/_quote/_empty_text.
  get availability_strip_variant(): AvailabilityStripVariant {
    const answer = this.evaluation_next_availability?.answer?.trim();
    if (!answer) return "empty";
    switch (this.evaluation_next_availability?.status) {
      case "Cleared":
        return "positive";
      case "Blocked":
        return "warning";
      case "Needs Review":
        return "followup";
      default:
        return "empty";
    }
  }

  // Line 1: a short caption naming the next-interview status. Null in the
  // empty variant, which renders as a single plain line instead (availability_empty_text).
  get availability_caption(): string | null {
    switch (this.availability_strip_variant) {
      case "positive":
        return "Availability for the next interview";
      case "warning":
        return "Limited availability for the next interview";
      case "followup":
        return "Availability for the next interview, needs follow-up";
      default:
        return null;
    }
  }

  get availability_said_at_string(): string | null {
    if (!this.evaluation_next_availability?.said_at) return null;
    return format(
      this.get_local_time(this.evaluation_next_availability.said_at),
      "MMM d",
    );
  }

  // The candidate's verbatim answer, unformatted -- null in the empty variant (same
  // predicate as availability_caption/_quote), so a caller (e.g. the interview-scheduling
  // mailto body) can't mention availability the UI treats as unassessed/absent.
  get recognized_availability_answer(): string | null {
    if (this.availability_strip_variant === "empty") return null;
    return this.evaluation_next_availability?.answer?.trim() ?? null;
  }

  // Line 2: the candidate's verbatim answer, wrapped in typographic quotes. Null
  // in the empty variant (same reasoning as availability_caption).
  get availability_quote(): string | null {
    const answer = this.recognized_availability_answer;
    return answer ? `“${answer}”` : null;
  }

  // Line 3: names the source of the quote above. Null when the screening call
  // has no said_at, or in the empty variant (same reasoning as availability_quote).
  get availability_attribution(): string | null {
    if (this.availability_strip_variant === "empty") return null;
    const said = this.availability_said_at_string;
    return said ? `From the screening call on ${said}` : null;
  }

  get availability_empty_text(): string {
    return NO_AVAILABILITY_CAPTURED_TEXT;
  }

  get checkpoints(): EvaluationCheckpoint[] {
    return (this.evaluation_checkpoints ?? []).map(
      (cp) => new EvaluationCheckpoint(cp),
    );
  }
}
